import { useState, FormEvent } from 'react';
import { useCampaign, Campaign, DiscountType, CampaignTarget } from '@/context/CampaignContext';
import { products } from '@/mocks/products';
import useScrollLock from '@/hooks/useScrollLock';

interface Props {
  campaign: Campaign | null;
  dark: boolean;
  onClose: () => void;
}

const today = () => new Date().toISOString().split('T')[0];
const nextMonth = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 1);
  return d.toISOString().split('T')[0];
};

const CATEGORIES = ['lithium', 'agm', 'gel'] as const;

export default function CampaignFormModal({ campaign, dark, onClose }: Props) {
  const { addCampaign, updateCampaign } = useCampaign();
  const isEdit = !!campaign;
  useScrollLock();

  const [form, setForm] = useState<Omit<Campaign, 'id'>>({
    name: campaign?.name || '',
    description: campaign?.description || '',
    discountType: campaign?.discountType || 'percentage',
    discountValue: campaign?.discountValue || 10,
    target: campaign?.target || 'all',
    targetCategories: campaign?.targetCategories || [],
    productIds: campaign?.productIds || [],
    active: campaign?.active ?? true,
    startDate: campaign?.startDate || today(),
    endDate: campaign?.endDate || nextMonth(),
    badgeLabel: campaign?.badgeLabel || '',
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const toggleProduct = (id: string) =>
    setForm((f) => ({
      ...f,
      productIds: f.productIds.includes(id) ? f.productIds.filter((x) => x !== id) : [...f.productIds, id],
    }));

  const toggleCategory = (cat: string) =>
    setForm((f) => ({
      ...f,
      targetCategories: (f.targetCategories || []).includes(cat)
        ? (f.targetCategories || []).filter((x) => x !== cat)
        : [...(f.targetCategories || []), cat],
    }));

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (isEdit && campaign) {
      updateCampaign({ ...form, id: campaign.id });
    } else {
      addCampaign(form);
    }
    onClose();
  };

  const inputCls = dark
    ? 'bg-[#0D1B2A] border-white/10 text-white placeholder-white/30 focus:border-[#F97316]'
    : 'bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#F97316]';
  const labelCls = `block text-xs font-semibold uppercase tracking-wide mb-1.5 ${dark ? 'text-white/60' : 'text-gray-500'}`;
  const fieldCls = `w-full border rounded-lg px-3 py-2.5 text-sm outline-none transition-colors ${inputCls}`;
  const text = dark ? 'text-white' : 'text-gray-900';
  const muted = dark ? 'text-white/50' : 'text-gray-500';
  const borderCls = dark ? 'border-white/10' : 'border-gray-100';
  const sectionBg = dark ? 'bg-[#0D1B2A]/60' : 'bg-gray-50';

  const samplePrice = 849;
  const previewPrice = form.discountType === 'percentage'
    ? Math.round(samplePrice * (1 - form.discountValue / 100))
    : Math.max(0, samplePrice - form.discountValue);

  const targetOptions: { value: CampaignTarget; label: string; icon: string; desc: string }[] = [
    { value: 'all',                  label: 'All Products',          icon: 'ri-grid-line',               desc: 'Every product in catalog' },
    { value: 'lithium',              label: 'Lithium Only',          icon: 'ri-flashlight-line',         desc: 'LiFePO4 series' },
    { value: 'agm',                  label: 'AGM Only',              icon: 'ri-battery-2-charge-line',   desc: 'AGM series' },
    { value: 'gel',                  label: 'Gel Only',              icon: 'ri-drop-line',               desc: 'Gel series' },
    { value: 'specific',             label: 'Specific Products',     icon: 'ri-checkbox-multiple-line',  desc: 'Hand-pick products' },
    { value: 'category_and_specific', label: 'Category + Specific', icon: 'ri-focus-3-line',            desc: 'Mix categories & products' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className={`w-full max-w-2xl max-h-[92vh] overflow-y-auto rounded-xl ${dark ? 'bg-[#1C2B3A]' : 'bg-white'}`}>
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-4 border-b ${borderCls}`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#F97316]/20">
              <i className="ri-megaphone-line text-[#F97316]"></i>
            </div>
            <h2 className={`font-bold text-base ${text}`}>{isEdit ? 'Edit Campaign' : 'New Discount Campaign'}</h2>
          </div>
          <button onClick={onClose} className={`w-8 h-8 flex items-center justify-center rounded-lg cursor-pointer ${dark ? 'text-white/50 hover:text-white hover:bg-white/10' : 'text-gray-400 hover:text-gray-900 hover:bg-gray-100'}`}>
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">

          {/* Name + Badge */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Campaign Name *</label>
              <input required className={fieldCls} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Summer Sale 2025" />
            </div>
            <div>
              <label className={labelCls}>Badge Label (on product card)</label>
              <input className={fieldCls} value={form.badgeLabel} onChange={(e) => set('badgeLabel', e.target.value)} placeholder="Summer Sale" />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className={labelCls}>Internal Note</label>
            <input className={fieldCls} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Optional internal note..." />
          </div>

          {/* Discount */}
          <div className={`rounded-lg p-4 border ${borderCls} ${sectionBg}`}>
            <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${muted}`}>
              <i className="ri-percent-line mr-1.5 text-[#F97316]"></i>
              Discount Settings
            </p>
            <div className="grid grid-cols-2 gap-4 mb-3">
              <div>
                <label className={labelCls}>Type</label>
                <select className={`${fieldCls} cursor-pointer`} value={form.discountType} onChange={(e) => set('discountType', e.target.value as DiscountType)}>
                  <option value="percentage">Percentage (% OFF)</option>
                  <option value="fixed">Fixed Amount ($ OFF)</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>{form.discountType === 'percentage' ? 'Discount %' : 'Amount ($)'}</label>
                <input type="number" required min={1} max={form.discountType === 'percentage' ? 99 : undefined} className={fieldCls} value={form.discountValue} onChange={(e) => set('discountValue', Number(e.target.value))} />
              </div>
            </div>
            <div className={`flex items-center gap-3 text-sm ${muted}`}>
              <i className="ri-price-tag-3-line text-[#F97316]"></i>
              <span>Example: $849 →</span>
              <span className="line-through">${samplePrice}</span>
              <span className="text-[#F97316] font-black text-base">${previewPrice}</span>
              {form.discountType === 'percentage' && <span className="text-emerald-400 text-xs font-bold">Save {form.discountValue}%</span>}
            </div>
          </div>

          {/* Target */}
          <div className={`rounded-lg p-4 border ${borderCls} ${sectionBg}`}>
            <p className={`text-xs font-bold uppercase tracking-widest mb-3 ${muted}`}>
              <i className="ri-focus-3-line mr-1.5 text-[#F97316]"></i>
              Apply To
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {targetOptions.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => set('target', opt.value)}
                  className={`flex flex-col items-start gap-1 py-2.5 px-3 rounded-lg text-xs font-semibold transition-all cursor-pointer border text-left ${
                    form.target === opt.value
                      ? 'bg-[#F97316] text-white border-[#F97316]'
                      : dark ? 'border-white/10 text-white/50 hover:text-white hover:border-white/20' : 'border-gray-200 text-gray-500 hover:text-gray-900 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-center gap-1.5">
                    <i className={`${opt.icon} text-sm`}></i>
                    <span>{opt.label}</span>
                  </div>
                  <span className={`text-[10px] font-normal ${form.target === opt.value ? 'text-white/70' : muted}`}>{opt.desc}</span>
                </button>
              ))}
            </div>

            {/* Category picker for category_and_specific */}
            {form.target === 'category_and_specific' && (
              <div className="mb-4">
                <p className={`text-xs font-semibold ${muted} mb-2`}>Select categories:</p>
                <div className="flex gap-2 flex-wrap">
                  {CATEGORIES.map((cat) => {
                    const sel = (form.targetCategories || []).includes(cat);
                    return (
                      <button
                        key={cat}
                        type="button"
                        onClick={() => toggleCategory(cat)}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all cursor-pointer border capitalize ${
                          sel ? 'bg-[#F97316] text-white border-[#F97316]' : dark ? 'border-white/20 text-white/60 hover:border-white/40' : 'border-gray-200 text-gray-600 hover:border-gray-400'
                        }`}
                      >
                        {sel && <i className="ri-check-line"></i>}
                        {cat}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Product picker */}
            {(form.target === 'specific' || form.target === 'category_and_specific') && (
              <div>
                <p className={`text-xs font-semibold ${muted} mb-2`}>
                  {form.target === 'category_and_specific' ? 'Also include specific products:' : 'Select products:'}
                </p>
                <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto pr-1">
                  {products.map((p) => {
                    const selected = form.productIds.includes(p.id);
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => toggleProduct(p.id)}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all cursor-pointer border ${
                          selected ? 'border-[#F97316] bg-[#F97316]/10' : dark ? 'border-white/10 hover:border-white/20' : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className={`w-5 h-5 flex items-center justify-center rounded border-2 shrink-0 transition-colors ${selected ? 'bg-[#F97316] border-[#F97316]' : dark ? 'border-white/30' : 'border-gray-300'}`}>
                          {selected && <i className="ri-check-line text-white text-xs"></i>}
                        </div>
                        <div className="w-8 h-8 rounded overflow-hidden shrink-0">
                          <img src={p.image} alt={p.name} className="w-full h-full object-cover object-top" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${text}`}>{p.name}</p>
                          <p className={`text-xs ${muted}`}>${p.price.toLocaleString()} · {p.category}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Dates */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>Start Date *</label>
              <input type="date" required className={fieldCls} value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
            </div>
            <div>
              <label className={labelCls}>End Date *</label>
              <input type="date" required className={fieldCls} value={form.endDate} onChange={(e) => set('endDate', e.target.value)} />
            </div>
          </div>

          {/* Active */}
          <div className={`flex items-center justify-between p-4 rounded-lg border ${borderCls} ${sectionBg}`}>
            <div>
              <p className={`text-sm font-semibold ${text}`}>Activate Campaign</p>
              <p className={`text-xs ${muted}`}>Discounts apply within the date range when active.</p>
            </div>
            <button type="button" onClick={() => set('active', !form.active)} className={`relative w-12 h-6 rounded-full transition-colors cursor-pointer ${form.active ? 'bg-[#F97316]' : dark ? 'bg-white/20' : 'bg-gray-200'}`}>
              <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${form.active ? 'left-7' : 'left-1'}`}></span>
            </button>
          </div>

          {/* Actions */}
          <div className={`flex gap-3 pt-2 border-t ${borderCls}`}>
            <button type="button" onClick={onClose} className={`flex-1 py-2.5 rounded-lg text-sm font-semibold cursor-pointer whitespace-nowrap border transition-colors ${dark ? 'border-white/20 text-white/70 hover:bg-white/10' : 'border-gray-200 text-gray-600 hover:bg-gray-100'}`}>
              Cancel
            </button>
            <button type="submit" className="flex-1 py-2.5 rounded-lg text-sm font-bold bg-[#F97316] text-white hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap">
              {isEdit ? 'Save Changes' : 'Create Campaign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
